/**
 * « Votre prochaine séance » : suites de la confirmation de réservation (SPEC CO4, CO5, CO6), sous les boutons
 * « Ajouter à mon agenda » / « Réserver un autre cours ». Une liste de liens (un arrêt de tabulation par ligne,
 * ouverture dans un nouvel onglet possible) : le même cours la semaine prochaine, un autre cours avec le coach, puis un
 * autre cours au studio (demande de Daphné du 08/10).
 *
 * Après le premier affichage de la confirmation, rien de visible ne bouge (revue du 09/10 : bloc inséré sous les
 * boutons défilés au milieu de l'écran, CLS 0,083) :
 * - suites connues au premier affichage : peintes avec la confirmation ;
 * - pas encore connues : une PLACE RÉSERVÉE, invisible, de la hauteur d'un bloc de trois lignes (deux si la ligne du
 *   studio ne peut pas apparaître : `studioSlot`), aux libellés et détails les plus longs, est peinte avec la
 *   confirmation ; les suites arrivées s'y affichent (en fondu, à la hauteur de la place : rien ne bouge). La hauteur
 *   en trop (moins de lignes que la place, ou aucune suite) n'est rendue que lorsque le contenu qui suit, pied de page
 *   compris, reste sous le bas de l'écran (lib/insertion.ts) ; en attendant, un espace vide. Plus de lignes que la place
 *   (site cru à un seul studio) : la ligne du studio attend que le bloc soit sous le bas de l'écran (lib/followUps.ts
 *   fitToSlot) ; bloc arrivé plus HAUT que la place (prénom plus long que l'exemple, autre police) : sa dernière ligne
 *   attend de même (withoutLastRow), avant que l'écran soit peint. Toujours rien au bout de 20 s : abandon ;
 * - lignes changées après coup (visites relues après la réservation : le cours de la semaine prochaine est en fait
 *   déjà réservé) : mises à jour quand le bloc est entièrement sous le bas de l'écran ; un simple changement de texte
 *   des mêmes lignes est appliqué tout de suite ;
 * - suites apparues sans place réservée : insérées quand le contenu qui suit est sous le bas de l'écran.
 * Vérifié tout de suite, puis à chaque défilement, redimensionnement ou changement de taille de la page.
 */
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { Link } from 'react-router-dom';
import {
  fitToSlot,
  followUpRows as rowsOf,
  studioTitle,
  WIDEST_NEXT_CLASS_DETAIL,
  withoutLastRow,
  type CoachOffer,
  type FollowUpOffers as Offers,
  type NextWeekOffer,
  type StudioOffer,
} from '../lib/followUps';
import { canResizeWithoutShift, watchUntil } from '../lib/insertion';
import { openFollowUpPlanning } from '../useFollowUps';
import { Icon } from './Icon';

const NBSP = '\u00a0';

/** Attente maximale des suites dans la place réservée (lectures du planning lentes ou bloquées). */
const SLOT_TIMEOUT_MS = 20_000;

const isPlainClick = (event: MouseEvent) =>
  event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

/**
 * Icônes des pastilles des trois lignes : même pastille teintée, même trait RENDU (1,5 px), même encombrement à l'œil.
 * - « En boucle » (même cours) : 20 px, trait 1,8.
 * - Silhouette (coach) : celle de la ligne « Coach » de la fiche cours (Panel), sans initiale ni photo : le prénom est
 *   dans le libellé (décision du 09/10, SPEC § 13.6). Son tracé n'occupe que 16 × 18 des 24 unités du carré (« en
 *   boucle » : 18 × 20) : en 20 px, elle paraissait plus petite et plus légère que sa voisine (encre 15 × 17 px contre
 *   17 × 18,5 ; relecture du 09/10). Dessinée en 22 px, trait ramené à 1,5 × 24 / 22 = 1,636 : même trait rendu,
 *   encre 17 × 18 px.
 * - Repère (studio, demande de Daphné du 08/10, SPEC § 13.8) : celui de la ligne « Studio » de la fiche cours (Panel),
 *   `pin`. Son tracé occupe 16 × 20 des 24 unités du carré, la même hauteur que « en boucle » (18 × 20) : 20 px, trait
 *   1,8, comme elle (encre 15 × 18,5 px ; en 22 px, il dépasserait ses voisines de 1,5 px en hauteur). Descendu de
 *   0,5 px (`pinBadge`, SPEC § 13.9) : tête lourde et pointe fine, centré par sa boîte d'encre il paraissait trop haut
 *   (barycentre à 0,9 px au-dessus du centre de la pastille, contre 0,02 px pour ses voisines).
 * Tailles paires, comme les pastilles (42 px, 38 px) : icône à un nombre entier de pixels du bord. En 19 px, Chromium
 * la calait 0,5 px trop haut ou trop bas selon la ligne (mesuré à l'encre : jusqu'à 1 px d'écart entre les lignes).
 */
const RENDERED_STROKE = 1.5;
const rowIcon = (size: number) => ({ size, stroke: +((RENDERED_STROKE * 24) / size).toFixed(3) });
const NEXT_WEEK_ICON = rowIcon(20);
const COACH_ICON = rowIcon(22);
const STUDIO_ICON = rowIcon(20);

/**
 * Place réservée : même structure que le bloc, invisible et inerte ; deux lignes aux libellés les plus longs, et la
 * ligne du studio du cours (son vrai libellé : même nombre de lignes de texte que la ligne à venir) quand elle peut
 * apparaître. Détails des lignes du coach et du studio : le plus large possible (WIDEST_NEXT_CLASS_DETAIL, « dimanche
 * 27 sept. ») ; « vendredi 16 oct. », plus étroit, comptait une ligne de texte de moins qu'un dimanche entre 389 et
 * 402 px de large (relecture du 09/10). Prénoms : exemples (« Jean-Marc ») ; un prénom plus long est rattrapé à
 * l'arrivée (withoutLastRow).
 */
function Slot({ sectionRef, studio }: { sectionRef: RefObject<HTMLElement>; studio: string | null }) {
  const row = (label: ReactNode, detail: string) => (
    <li>
      <div className="mmv2-next__item">
        <span className="mmv2-next__icon" />
        <span className="mmv2-next__text">
          <span className="mmv2-next__label">{label}</span>
          <span className="mmv2-next__meta">
            <span className="mmv2-next__detail">{detail}</span>
          </span>
        </span>
        <span className="mmv2-next__chevron">
          <Icon name="right" size={18} />
        </span>
      </div>
    </li>
  );
  return (
    <section ref={sectionRef} className="mmv2-next mmv2-next--slot" aria-hidden="true" data-followups-held="">
      <h2 className="mmv2-kicker mmv2-next__title">Votre prochaine séance</h2>
      <ul className="mmv2-next__list">
        {row('Réserver le même cours la semaine prochaine', `Vendredi 16${NBSP}oct.${NBSP}· 07:45${NBSP}· avec${NBSP}Jean-Marc`)}
        {row(`Réserver un autre cours avec${NBSP}Jean-Marc`, WIDEST_NEXT_CLASS_DETAIL)}
        {studio !== null ? row(studioTitle(studio), WIDEST_NEXT_CLASS_DETAIL) : null}
      </ul>
    </section>
  );
}

/** Ligne du bloc : lien, pastille et son icône, libellé, détail (étiquette d'état éventuelle), chevron. */
function Row({
  to,
  followup,
  state,
  icon,
  title,
  detail,
  tag = null,
  onOpen,
}: {
  to: string;
  followup: 'next-week' | 'coach' | 'studio';
  state?: string;
  icon: ReactNode;
  title: ReactNode;
  detail: string;
  tag?: NextWeekOffer['tag'];
  onOpen?: () => void;
}) {
  return (
    <li>
      <Link
        className="mmv2-next__item"
        to={to}
        data-followup={followup}
        data-state={state}
        onClick={
          onOpen
            ? (event) => {
                if (isPlainClick(event)) onOpen();
              }
            : undefined
        }
      >
        <span className="mmv2-next__icon" aria-hidden="true">
          {icon}
        </span>
        <span className="mmv2-next__text">
          <span className="mmv2-next__label">{title}</span>
          <span className="mmv2-next__meta">
            <span className="mmv2-next__detail">{detail}</span>
            {tag ? <span className={`mmv2-tag mmv2-tag--${tag.tone} mmv2-next__tag`}>{tag.label}</span> : null}
          </span>
        </span>
        <span className="mmv2-next__chevron" aria-hidden="true">
          <Icon name="right" size={18} />
        </span>
      </Link>
    </li>
  );
}

export function FollowUps({
  ready,
  nextWeek,
  coach,
  studio = null,
  studioSlot = null,
}: {
  ready: boolean;
  nextWeek: NextWeekOffer | null;
  coach: CoachOffer | null;
  studio?: StudioOffer | null;
  /** Nom du studio pour la place réservée (troisième ligne) ; null : place de deux lignes (useFollowUps). */
  studioSlot?: string | null;
}) {
  const incoming = useMemo<Offers | null>(
    () => (ready && (nextWeek || coach || studio) ? { nextWeek, coach, studio } : null),
    [ready, nextWeek, coach, studio],
  );
  // Lignes affichées. Monté avec la confirmation : suites connues à ce moment-là → peintes avec elle.
  const [view, setView] = useState<Offers | null>(incoming);
  const [late] = useState(!incoming);
  // Place réservée tant que les suites ne sont pas connues au premier affichage ; hauteur gardée (px) ensuite.
  const [slotted, setSlotted] = useState(!ready);
  // Ligne du studio dans la place réservée : décidée au premier affichage, comme la place elle-même.
  const [slotStudio] = useState(studioSlot);
  const [minHeight, setMinHeight] = useState<number | null>(null);
  const [gaveUp, setGaveUp] = useState(false);
  const [shown, setShown] = useState(false);
  const markerRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Suites arrivées dans la place réservée : affichées tout de suite, à la hauteur de la place.
  useLayoutEffect(() => {
    if (!slotted || view || !incoming) return;
    // Hauteur exacte (fractionnaire) : offsetHeight, arrondi, laisserait le bloc grandir d'une fraction de pixel.
    setMinHeight(sectionRef.current ? sectionRef.current.getBoundingClientRect().height : null);
    // Plus de lignes que la place : la ligne du studio attend (lignes changées après coup, plus bas).
    setView(fitToSlot(incoming, slotStudio !== null ? 3 : 2));
    setSlotted(false);
  }, [slotted, view, incoming, slotStudio]);

  // Place réservée sans suite au bout de SLOT_TIMEOUT_MS : abandon (la place est rendue comme une place vide).
  useEffect(() => {
    if (!slotted || ready) return undefined;
    const timer = window.setTimeout(() => setGaveUp(true), SLOT_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [slotted, ready]);

  /*
   * Bloc arrivé plus haut que la place (prénom plus long que l'exemple, détail plus large dans une autre police) et
   * contenu qui suit à l'écran : la dernière ligne attend que le bloc soit sous le bas de l'écran (lignes changées après
   * coup, plus bas) au lieu de pousser ce contenu. Effet de mise en page : rien n'est peint entre-temps.
   */
  useLayoutEffect(() => {
    if (!view || minHeight === null) return;
    const section = sectionRef.current;
    if (!section) return;
    const growth = section.getBoundingClientRect().height - minHeight;
    if (canResizeWithoutShift(section, -growth, window.innerHeight)) return;
    const fewer = withoutLastRow(view);
    if (fewer) setView(fewer);
  }, [view, minHeight]);

  // Hauteur en trop rendue (place vide, ou bloc plus court que la place) quand rien de visible ne bouge.
  const emptySlot = slotted && !view && !incoming && (ready || gaveUp);
  const heldHeight = !!view && minHeight !== null;
  useEffect(() => {
    if (!emptySlot && !heldHeight) return undefined;
    return watchUntil(() => {
      const section = sectionRef.current;
      if (!section) return false;
      const rect = section.getBoundingClientRect();
      // Écart entre le contenu et la hauteur GARDÉE (pas la hauteur du bloc) : un bloc qui la dépasse encore (lignes en
      // trop, retirées juste après par l'effet de mise en page plus haut) ne la rend pas ; sinon, la ligne retirée, le
      // bloc rétrécirait à l'écran.
      const held = parseFloat(section.style.minHeight);
      const delta = heldHeight
        ? (listRef.current ? listRef.current.getBoundingClientRect().bottom - rect.top : rect.height) -
          (Number.isFinite(held) ? held : rect.height)
        : -(rect.height + (parseFloat(window.getComputedStyle(section).marginTop) || 0));
      if (!canResizeWithoutShift(section, delta, window.innerHeight)) return false;
      if (heldHeight) setMinHeight(null);
      else setSlotted(false);
      return true;
    });
  }, [emptySlot, heldHeight]);

  // Lignes ajoutées ou retirées après coup : quand le bloc est entièrement sous le bas de l'écran.
  const rowsChanged = !!view && rowsOf(view) !== rowsOf(incoming);
  useEffect(() => {
    if (!rowsChanged) return undefined;
    return watchUntil(() => {
      const section = sectionRef.current;
      if (!section || section.getBoundingClientRect().top < window.innerHeight) return false;
      setView(incoming);
      setMinHeight(null);
      return true;
    });
  }, [rowsChanged, incoming]);

  // Mêmes lignes : texte mis à jour tout de suite.
  useEffect(() => {
    if (view && incoming && view !== incoming && rowsOf(view) === rowsOf(incoming)) setView(incoming);
  }, [view, incoming]);

  // Suites apparues sans place réservée : insertion quand le contenu qui suit le repère est sous le bas de l'écran.
  const inserting = !view && !slotted && !!incoming;
  useEffect(() => {
    if (!inserting) return undefined;
    return watchUntil(() => {
      const marker = markerRef.current;
      if (!marker || !canResizeWithoutShift(marker, 1, window.innerHeight)) return false;
      setView(incoming);
      return true;
    });
  }, [inserting, incoming]);

  const visible = !!view;
  useEffect(() => {
    if (!visible) return undefined;
    const frame = window.requestAnimationFrame(() => setShown(true));
    return () => window.cancelAnimationFrame(frame);
  }, [visible]);

  if (!view) {
    if (slotted) return <Slot sectionRef={sectionRef} studio={slotStudio} />;
    return incoming ? <div ref={markerRef} className="mmv2-next-marker" data-followups-held="" aria-hidden="true" /> : null;
  }

  const { nextWeek: nextWeekRow, coach: coachRow, studio: studioRow } = view;
  const style: CSSProperties | undefined = minHeight !== null ? { minHeight } : undefined;
  return (
    <section
      ref={sectionRef}
      className="mmv2-next"
      style={style}
      aria-labelledby="mmv2-next-title"
      data-shown={shown || undefined}
      data-placement={late ? 'late' : 'inline'}
      data-followups-held={rowsChanged || minHeight !== null ? '' : undefined}
    >
      <h2 id="mmv2-next-title" className="mmv2-kicker mmv2-next__title">
        Votre prochaine séance
      </h2>
      <ul ref={listRef} className="mmv2-next__list">
        {nextWeekRow ? (
          <Row
            to={nextWeekRow.route}
            followup="next-week"
            state={nextWeekRow.state}
            icon={<Icon name="repeat" {...NEXT_WEEK_ICON} />}
            title={nextWeekRow.title}
            detail={nextWeekRow.detail}
            tag={nextWeekRow.tag}
          />
        ) : null}
        {coachRow ? (
          <Row
            to={coachRow.route}
            followup="coach"
            icon={<Icon name="user" {...COACH_ICON} />}
            title={coachRow.title}
            detail={coachRow.detail}
            onOpen={() => openFollowUpPlanning(coachRow)}
          />
        ) : null}
        {studioRow ? (
          <Row
            to={studioRow.route}
            followup="studio"
            icon={<Icon name="pinBadge" {...STUDIO_ICON} />}
            title={studioRow.title}
            detail={studioRow.detail}
            onOpen={() => openFollowUpPlanning(studioRow)}
          />
        ) : null}
      </ul>
    </section>
  );
}
